import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { prepareData } from 'helpers/prepare-req-data'
import { prepParams } from 'helpers/prep-req-url'
import { getRespData  } from 'helpers/get-resp-data'
import client from 'api'
import _ from 'lodash'

import { v4 as uuid4 } from 'uuid'


const initialState = {
    loadSicherungListStatus: 'idle',
    loadSicherungListError: undefined as any,
    loadSicherungListLastArgs: undefined as any,
    loadSicherungStatus: 'idle',
    loadSicherungError: undefined as any,
    saveSicherungStatus: 'idle',
    saveSicherungError: undefined as any,
    deleteSicherungStatus: 'idle',
    deleteSicherungError: undefined as any,

    sicherungList: [] as any[],
    formItem: undefined as any,
    filterValues: {} as any,
    filterLookupValues: {} as any,

    directLoad: false,
    load: false,
    activeDivRef: null as any,

    filterValuesId: null,
    valuesFromDB: false,
    filterVisibleId: null,
    filterVisible: true,
}


export const loadSicherungList = createAsyncThunk('auftragSicherungSlice/loadSicherungList', async ({ parentModel, forceReload }: any, { rejectWithValue, getState }: any) => {
    const lastArgs = getState().auftragSicherungSlice.loadSicherungListLastArgs
    if (_.isEqual(lastArgs, { parentModel }) && !forceReload) { return }

    let promises = []
    promises.push(client.get(
        `/api/ixweb/sicherung/?action=gte&AUFTRAGID_FILTER=${parentModel.HDR_ID}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: value1.map((x: any) => ({
            ...x,
        })),
        args: { parentModel }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadSicherungListStatus } = getState().auftragSicherungSlice
        return loadSicherungListStatus !== 'pending'
    }
})

export const loadSicherung = createAsyncThunk('auftragSicherungSlice/loadSicherung', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.get(
        `/api/ixweb/sicherung/?action=eql&HDR_ID=${id}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadSicherungStatus } = getState().auftragSicherungSlice
        return loadSicherungStatus !== 'pending'
    }
})

export const saveSicherung = createAsyncThunk('auftragSicherungSlice/saveSicherung', async ({ sicherung }: any, { rejectWithValue }: any) => {
    let promises = []
    if (sicherung?.HDR_ID) {
        promises.push(client.put(
            `/api/ixweb/sicherung/${ sicherung?.HDR_ID }/`,
            prepareData(sicherung),
        ))
    } else {
        promises.push(client.post(
            `/api/ixweb/sicherung/`,
            prepareData(sicherung),
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { saveSicherungStatus } = getState().auftragSicherungSlice
        return saveSicherungStatus !== 'pending'
    }
})

export const deleteSicherung = createAsyncThunk('auftragSicherungSlice/deleteSicherung', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.delete(
        `/api/ixweb/sicherung/${id}/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: id,
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { deleteSicherungStatus } = getState().auftragSicherungSlice
        return deleteSicherungStatus !== 'pending'
    }
})

const SicherungSlice = createSlice({
    name: 'auftragSicherungSlice',
    initialState,
    reducers: {
        resetloadSicherungListError: (state) => {
            state.loadSicherungListError = undefined
        },
        resetloadSicherung: (state) => {
            state.formItem = undefined
            state.loadSicherungError = undefined
        },
        resetloadSicherungError: (state) => {
            state.loadSicherungError = undefined
        },
        resetsaveSicherung: (state) => {
            state.formItem = undefined
            state.saveSicherungError = undefined
        },
        resetsaveSicherungError: (state) => {
            state.saveSicherungError = undefined
        },
        resetdeleteSicherungError: (state) => {
            state.deleteSicherungError = undefined
        },
        removeFromSicherungListById: (state, action) => {
            state.sicherungList = state.sicherungList.filter((x: any) => x.HDR_ID !== action.payload)
        },
        removeFromSicherungListByIds: (state, action) => {
            state.sicherungList = state.sicherungList.filter((x: any) => action.payload.indexOf(x.HDR_ID) === -1)
        },
        setFilterValues: (state, action) => {
            if (action.payload === undefined || action.payload === null) {
                state.filterValues = {}
            } else {
                state.filterValues = { ...state.filterValues, ...action.payload }
            }
        },
        setFilterLookupValue: (state, action) => {
            const { name, value, formValue } = action.payload
            if (name) {
                state.filterLookupValues[name] = value
                state.filterValues[name] = formValue
            }
        },
        setDirectLoad: (state, action) => { state.directLoad = action.payload },
        setLoad: (state, action) => { state.load = action.payload },
        setActiveDivRef: (state, action) => { state.activeDivRef = action.payload},
        setFilterValuesId: (state, action) => {
            state.filterValuesId = action.payload
        },
        setValuesFromDB: (state, action) => {
            state.valuesFromDB = action.payload
        },
        setFilterVisibleId: (state, action) => {
            state.filterVisibleId = action.payload
        },
        setFilterVisible: (state, action) => {
            state.filterVisible = action.payload
        },
        preloadData: (state, action) => {
            state.sicherungList = action.payload.map((x: any) => ({ ...x, HDR_ID: uuid4()}))
            state.loadSicherungListStatus = 'preloaded'
            state.loadSicherungListLastArgs = ''
        },
        clearPreloadedData: (state) => {
            state.sicherungList = []
            state.loadSicherungListStatus = 'idle'
            state.loadSicherungListLastArgs = undefined
        },
    },
    extraReducers: builder => {
        builder.addCase(loadSicherungList.pending, (state) => {
            state.loadSicherungListStatus = 'pending'
            state.loadSicherungListError = undefined
        })
        builder.addCase(loadSicherungList.fulfilled, (state, action) => {
            state.loadSicherungListStatus = 'idle'

            if (!action.payload) { return }

            state.loadSicherungListLastArgs = action.payload.args

            {
                state.sicherungList = action.payload.data

            }
        })
        builder.addCase(loadSicherungList.rejected, (state, action) => {
            state.loadSicherungListStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadSicherungListError = action.error.message
            } else {
                state.loadSicherungListError = action.payload
            }
        })

        builder.addCase(loadSicherung.pending, (state) => {
            state.loadSicherungStatus = 'pending'
            state.loadSicherungError = undefined
        })
        builder.addCase(loadSicherung.fulfilled, (state, action) => {
            state.loadSicherungStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.sicherungList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.sicherungList.push(newItem) }
                else { state.sicherungList[i] = { ...state.sicherungList[i], ...newItem } }
            }
        })
        builder.addCase(loadSicherung.rejected, (state, action) => {
            state.loadSicherungStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadSicherungError = action.error.message
            } else {
                state.loadSicherungError = action.payload
            }
        })

        builder.addCase(saveSicherung.pending, (state) => {
            state.saveSicherungStatus = 'pending'
            state.saveSicherungError = undefined
        })
        builder.addCase(saveSicherung.fulfilled, (state, action) => {
            state.saveSicherungStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.sicherungList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.sicherungList.push(newItem) }
                else { state.sicherungList[i] = { ...state.sicherungList[i], ...newItem } }
            }
        })
        builder.addCase(saveSicherung.rejected, (state, action) => {
            state.saveSicherungStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.saveSicherungError = action.error.message
            } else {
                state.saveSicherungError = action.payload
            }
        })

        builder.addCase(deleteSicherung.pending, (state) => {
            state.deleteSicherungStatus = 'pending'
            state.deleteSicherungError = undefined
        })
        builder.addCase(deleteSicherung.fulfilled, (state, action) => {
            state.deleteSicherungStatus = 'idle'

            {
                if (Array.isArray(action.payload.data)) {
                    state.sicherungList = state.sicherungList.filter((x: any) => !action.payload.data.includes(x.HDR_ID))
                } else {
                    state.sicherungList = state.sicherungList.filter((x: any) => x.HDR_ID !== action.payload.data)
                }
            }
        })
        builder.addCase(deleteSicherung.rejected, (state, action) => {
            state.deleteSicherungStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.deleteSicherungError = action.error.message
            } else {
                state.deleteSicherungError = action.payload
            }
        })

    }
})

export const {
    resetloadSicherungListError,
    resetloadSicherung,
    resetloadSicherungError,
    resetsaveSicherung,
    resetsaveSicherungError,
    resetdeleteSicherungError,
    removeFromSicherungListById,
    removeFromSicherungListByIds,
    setFilterValues,
    setFilterLookupValue,
    setDirectLoad,
    setLoad,
    setActiveDivRef,
    setFilterValuesId,
    setValuesFromDB,
    setFilterVisible,
    setFilterVisibleId,
    preloadData,
    clearPreloadedData,
} = SicherungSlice.actions
export default SicherungSlice.reducer
